import { X } from 'lucide-react';
import { Toast as T } from 'radix-ui';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { cn } from '../lib/cn';
import { useUi } from '../lib/ui-provider';

export interface ToastMessage {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly tone?: 'success' | 'danger' | 'info';
}

interface Entry extends ToastMessage {
  readonly id: number;
}

const ToastContext = createContext<((message: ToastMessage) => void) | null>(null);

/** Transient confirmation ("Invitation sent"). Errors that need action belong inline. */
export function Toaster(props: { children: ReactNode }) {
  const { labels } = useUi();
  const [entries, setEntries] = useState<Entry[]>([]);
  const push = useCallback((message: ToastMessage) => {
    setEntries((current) => [...current.slice(-2), { ...message, id: Date.now() + Math.random() }]);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {props.children}
      {entries.map((entry) => (
        <T.Root
          key={entry.id}
          type={entry.tone === 'danger' ? 'foreground' : 'background'}
          onOpenChange={(open) => {
            if (!open) setEntries((current) => current.filter((e) => e.id !== entry.id));
          }}
          className={cn(
            'bl-anim-toast flex items-start gap-3 rounded-lg border bg-surface p-4 shadow-overlay',
            entry.tone === 'danger' ? 'border-danger/40' : 'border-line',
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              'mt-1.5 size-2 shrink-0 rounded-full',
              entry.tone === 'danger'
                ? 'bg-danger'
                : entry.tone === 'info'
                  ? 'bg-info'
                  : 'bg-success',
            )}
          />
          <div className="min-w-0 flex-1">
            <T.Title className="text-sm font-medium text-ink">{entry.title}</T.Title>
            {entry.description ? (
              <T.Description className="mt-0.5 text-sm text-ink-muted">
                {entry.description}
              </T.Description>
            ) : null}
          </div>
          <T.Close aria-label={labels.close} className="text-ink-subtle hover:text-ink">
            <X className="size-4" aria-hidden="true" />
          </T.Close>
        </T.Root>
      ))}
      <T.Viewport className="fixed bottom-4 end-4 z-(--z-toast) flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2 outline-none" />
    </ToastContext.Provider>
  );
}

export function useToast(): (message: ToastMessage) => void {
  const push = useContext(ToastContext);
  return useMemo(() => push ?? (() => {}), [push]);
}
